export const API_ENDPOINTS = {
  // Auth
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  REFRESH_TOKEN: '/auth/refresh',

  // Call Logs
  CALL_LOGS: '/calls',
  CALL_LOG_DETAIL: (id: number) => `/calls/${id}`,

  // System health / observability
  ASTERISK_METRICS: '/telephony/asterisk-metrics',
  SOFTPHONE_CREDENTIALS: '/telephony/softphone/credentials',
  SOFTPHONE_TEST_TARGETS: '/telephony/softphone/test-targets',
  // The switches THIS user's organization may register a phone to. Distinct
  // from the test targets above, which are every enabled provider and exist to
  // place a diagnostic call — asking that list "which may I use" would offer
  // other tenants' switches and be refused at provisioning time.
  SOFTPHONE_PROVIDERS: '/telephony/softphone/providers',
  SOFTPHONE_CREDENTIALS_REISSUE: '/telephony/softphone/credentials/reissue',
  // May this user have a browser phone here at all — purchased seats only
  // (2026-10-14). Never 403s on entitlement; it explains instead.
  SOFTPHONE_ENTITLEMENT: '/telephony/softphone/entitlement',
  AI_AGENTS: '/agents',
  AI_AGENT_DETAIL: (id: number) => `/agents/${id}`,
  AI_AGENT_TOOLS: (agentId: number) => `/agents/${agentId}/tools`,
  AI_AGENT_TOOL_DETAIL: (agentId: number, toolId: number) => `/agents/${agentId}/tools/${toolId}`,
  AI_AGENT_DOCS: (agentId: number) => `/agents/${agentId}/documents`,
  AI_AGENT_DOC_DETAIL: (agentId: number, docId: number) => `/agents/${agentId}/documents/${docId}`,
  AI_AGENT_CHANNELS: (agentId: number) => `/agents/${agentId}/channels`,
  AI_AGENT_CHANNEL_DETAIL: (agentId: number, channelId: number) => `/agents/${agentId}/channels/${channelId}`,
  AI_AGENT_PROMPTS: (agentId: number) => `/agents/${agentId}/prompts`,
  AI_AGENT_PROMPT_DETAIL: (agentId: number, promptId: number) => `/agents/${agentId}/prompts/${promptId}`,
  MEMORY_PROFILES: '/agents/memory-profiles',
  MEMORY_PROFILE_DETAIL: (id: number) => `/agents/memory-profiles/${id}`,
  // Billing (Phase 5)
  BILLING_RATE_CARDS: '/billing/rate-cards',
  BILLING_RATE_CARD_DETAIL: (id: number) => `/billing/rate-cards/${id}`,
  BILLING_RATE_CARD_RATES: (id: number) => `/billing/rate-cards/${id}/rates`,
  BILLING_RATE_CARD_RATE_DETAIL: (id: number, rateId: number) => `/billing/rate-cards/${id}/rates/${rateId}`,
  BILLING_ACCOUNTS: '/billing/accounts',
  BILLING_ACCOUNT_TOPUP: (businessId: string) => `/billing/accounts/${businessId}/topup`,
  BILLING_INVOICES: '/billing/invoices',
  BILLING_INVOICE_GENERATE: '/billing/invoices/generate',
  BILLING_USAGE: '/billing/usage',
  BILLING_METER: '/billing/usage/meter',
  // Billing profiles (phase 1): who an organisation is on an invoice.
  BILLING_PROFILE_REFERENCE: '/billing/profiles/reference',
  BILLING_PROFILE: (organizationId: string) =>
    `/billing/profiles/organization/${encodeURIComponent(organizationId)}`,
  // Credit control: alerts, the daily statement, enforcement and its audit (per account).
  BILLING_ALERTS: (organizationId: string) =>
    `/billing/accounts/organization/${encodeURIComponent(organizationId)}/alerts`,
  BILLING_ALERT_SETTINGS: (organizationId: string) =>
    `/billing/accounts/organization/${encodeURIComponent(organizationId)}/alert-settings`,
  BILLING_DAILY_STATEMENT: (organizationId: string) =>
    `/billing/accounts/organization/${encodeURIComponent(organizationId)}/statements/daily`,
  BILLING_ENFORCEMENT: (organizationId: string) =>
    `/billing/accounts/organization/${encodeURIComponent(organizationId)}/enforcement`,
  BILLING_SUSPENSION_EVENTS: (organizationId: string) =>
    `/billing/accounts/organization/${encodeURIComponent(organizationId)}/suspension-events`,
  // Credit grants (expiring credit) and refunds.
  BILLING_GRANTS: '/billing/credits/grants',
  BILLING_GRANTS_EXPIRING: '/billing/credits/grants/expiring',
  BILLING_GRANT_VOID: (id: number) => `/billing/credits/grants/${id}/void`,
  BILLING_CREDIT_REFUND: '/billing/credits/refund',
  // Reports (read-only over the ledger).
  BILLING_INVOICE_PREVIEW: '/billing/invoices/preview',
  BILLING_USAGE_DASHBOARD: '/billing/usage/dashboard',
  BILLING_USAGE_TOP_BUSINESSES: '/billing/usage/top-businesses',
  BILLING_DEFERRED_REVENUE: '/billing/reports/deferred-revenue',
  BILLING_DEFERRED_REVENUE_XLSX: '/billing/reports/deferred-revenue/xlsx',
  // Test call numbers: our team's phones; their calls are never billed to the client.
  // My billing (self-service, role-aware): payable, receivable, wallet, statement.
  BILLING_MY_SUMMARY: '/billing/my/summary',
  BILLING_MY_BUSINESSES: '/billing/my/businesses',
  BILLING_MY_WALLET: '/billing/my/wallet',
  BILLING_MY_STATEMENT_XLSX: '/billing/my/statement.xlsx',
  // Calls the tata switches refused / capped for this organisation (switch pre-answer gate).
  BILLING_MY_REFUSALS: '/billing/my/refusals',
  BILLING_TEST_NUMBERS: '/billing/test-numbers',
  BILLING_TEST_NUMBER: (id: number) => `/billing/test-numbers/${id}`,
  BILLING_TEST_NUMBER_DEACTIVATE: (id: number) => `/billing/test-numbers/${id}/deactivate`,
  BILLING_TEST_CALL_REPORT: '/billing/test-numbers/report',
  BILLING_TEST_ADJUSTMENT_PREVIEW: '/billing/test-numbers/adjustment/preview',
  BILLING_TEST_ADJUSTMENT_APPLY: '/billing/test-numbers/adjustment/apply',
  SUPERVISION_LIVE_CHANNELS: '/telephony/supervision/live-channels',
  SUPERVISION_SPY: '/telephony/supervision/spy',
  SUPERVISION_HANGUP: '/telephony/supervision/hangup',
  POOL_STATS: '/admin/pools/stats',
  POOL_HEALTH: '/admin/pools/health',
  WEBHOOK_STATS: '/api/webhook/stats',
  WEBHOOK_HEALTH: '/api/webhook/health',

  // Call hangup metrics (failure / disposition observability)
  CALL_HANGUP_METRICS: '/calls/hangup-metrics',
  CALL_HANGUP_METRICS_SUMMARY: '/calls/hangup-metrics/summary',
  CALL_HANGUP_METRIC_DETAIL: (uniqueId: string) => `/calls/hangup-metrics/${encodeURIComponent(uniqueId)}`,

  // Audio Sessions
  AUDIO_SESSIONS: '/sessions',
  AUDIO_SESSION_DETAIL: (id: number) => `/sessions/${id}`,

  // Recordings
  RECORDINGS: '/recordings',
  RECORDING_DETAIL: (id: number) => `/recordings/${id}`,
  RECORDING_DOWNLOAD: (id: string) => `/recordings/${id}/download`,

  // Transfers
  TRANSFERS: '/transfers',
  TRANSFER_DETAIL: (id: number) => `/transfers/${id}`,

  // Phone Numbers
  PHONE_NUMBERS: '/phones',
  PHONE_NUMBER_DETAIL: (id: number) => `/phones/${id}`,

  // Transfer Profiles (AI transfer control plane)
  TRANSFER_PROFILES: '/telephony/transfer-profiles',
  TRANSFER_PROFILE_DETAIL: (id: number) => `/telephony/transfer-profiles/${id}`,

  // Provider registry (telephony control plane)
  PROVIDERS: '/telephony/providers',
  PROVIDER_DETAIL: (id: number) => `/telephony/providers/${id}`,
  PROVIDER_NUMBERS: '/telephony/providers/numbers',
  // Place an outbound call. Authenticated and tenant-scoped, unlike the switch's
  // own origination endpoint — which is why customers are pointed here.
  OUTBOUND_CALL: '/v1/telephony/outbound/call',
  /**
   * Caller IDs THIS organisation may present. Read from the same query the
   * origination guard authorises against — do NOT use PROVIDER_NUMBERS here,
   * that is the platform-wide inventory and ignores organizationId, so it
   * offered every number on the platform and the call was then refused.
   */
  OUTBOUND_NUMBERS: '/v1/telephony/outbound/numbers',
  OUTBOUND_CAPABILITY: (orgId: string) => `/v1/telephony/outbound/capability/${orgId}`,
  OUTBOUND_CAPABILITY_HISTORY: (orgId: string) => `/v1/telephony/outbound/capability/${orgId}/history`,
  PROVIDER_NUMBER_DETAIL: (id: number) => `/telephony/providers/numbers/${id}`,
  PROVIDER_BUSINESS_CONFIGS: '/telephony/providers/business-configs',
  PROVIDER_BUSINESS_CONFIG_DETAIL: (id: number) => `/telephony/providers/business-configs/${id}`,
  PROVIDER_RESOLVE: '/telephony/providers/resolve',

  /**
   * Number OWNERSHIP — a different table and a different question from
   * PROVIDER_NUMBERS' allocate/release pair above.
   *
   * /telephony/providers/numbers/{id}/allocate writes `business_id` and
   * `status` — the switch's routing label. These write
   * `purchased_by_organization_id` and append a `number_allocations` tenure,
   * which is what CallerIdAuthorizer and the outbound caller-ID picker read.
   * The two disagree by design; using the wrong one silently fails to move
   * ownership.
   *
   * Both mutations key on the numeric provider_numbers.id. Only the history
   * lookup takes the E.164 — passing the number to acquire/release addresses a
   * different row, or none.
   */
  NUMBER_ACQUIRE: (numberId: number) => `/v1/numbers/${numberId}/acquire`,
  NUMBER_RELEASE: (numberId: number) => `/v1/numbers/${numberId}/release`,
  NUMBER_HISTORY: (e164: string) => `/v1/numbers/${encodeURIComponent(e164)}/history`,
  NUMBERS_HELD_BY: (organizationId: string) => `/v1/numbers/held-by/${encodeURIComponent(organizationId)}`,
  // Retire a garbage number for good (never deleted) / put it back in the pool.
  NUMBER_RETIRE: (numberId: number) => `/v1/numbers/${numberId}/retire`,
  NUMBER_UNRETIRE: (numberId: number) => `/v1/numbers/${numberId}/unretire`,

  // Manual "Push to switch": writes a number's label in its home switch's
  // ivr_dialing_info. MASTER_ADMIN / PLATFORM_ADMIN. Refusals are 409s.
  // Number lifecycle (Number Ownership: add / allot / push with flow / release / retire /
  // details drawer / settings). Master/platform + Green Giraffe root admins; 403 otherwise.
  NUMBER_LIFECYCLE_MODE: '/v1/numbers/lifecycle/mode',
  NUMBER_LIFECYCLE_ADD: '/v1/numbers/lifecycle/add',
  NUMBER_LIFECYCLE_FLOWS: '/v1/numbers/lifecycle/flows',
  NUMBER_LIFECYCLE: (numberId: number) => `/v1/numbers/lifecycle/${numberId}`,
  NUMBER_LIFECYCLE_ALLOT_PREVIEW: (numberId: number) => `/v1/numbers/lifecycle/${numberId}/allot/preview`,
  NUMBER_LIFECYCLE_ALLOT: (numberId: number) => `/v1/numbers/lifecycle/${numberId}/allot`,
  NUMBER_LIFECYCLE_PUSH_PREVIEW: (numberId: number) => `/v1/numbers/lifecycle/${numberId}/push/preview`,
  NUMBER_LIFECYCLE_PUSH_APPLY: (numberId: number) => `/v1/numbers/lifecycle/${numberId}/push/apply`,
  NUMBER_LIFECYCLE_RELEASE: (numberId: number) => `/v1/numbers/lifecycle/${numberId}/release`,
  NUMBER_LIFECYCLE_RETIRE: (numberId: number) => `/v1/numbers/lifecycle/${numberId}/retire`,
  NUMBER_LIFECYCLE_PATTERN: (numberId: number) => `/v1/numbers/lifecycle/${numberId}/billing-pattern`,
  NUMBER_LIFECYCLE_ROLLBACK: (pushId: number) => `/v1/numbers/lifecycle/pushes/${pushId}/rollback`,
  NUMBER_LIFECYCLE_SETTINGS: (numberId: number) => `/v1/numbers/lifecycle/${numberId}/settings`,
  NUMBER_LIFECYCLE_SETTINGS_PREVIEW: (numberId: number) => `/v1/numbers/lifecycle/${numberId}/settings/preview`,
  NUMBER_LIFECYCLE_SETTINGS_APPLY: (numberId: number) => `/v1/numbers/lifecycle/${numberId}/settings/apply`,
  NUMBER_LIFECYCLE_OUTBOUND: (numberId: number) => `/v1/numbers/lifecycle/${numberId}/settings/outbound`,
  NUMBER_LIFECYCLE_CALLER_LOOKUP: (numberId: number) => `/v1/numbers/lifecycle/${numberId}/settings/caller-lookup`,
  SWITCH_PUSH_MODE: '/v1/numbers/switch-push/mode',
  SWITCH_PUSH_PREVIEW: '/v1/numbers/switch-push/preview',
  SWITCH_PUSH_APPLY: '/v1/numbers/switch-push/apply',
  SWITCH_PUSH_HISTORY: '/v1/numbers/switch-push/history',
  SWITCH_PUSH_ROLLBACK: (pushId: number) => `/v1/numbers/switch-push/${pushId}/rollback`,

  // Provider reassign (Number Ownership, provider view): a provider moves numbers it holds
  // between its own org and its businesses. SUPER_ADMIN / SERVICE_ADMIN / PROVIDER_OWNER /
  // PROVIDER_ADMIN (+ master). Tree enforced server-side: 403 outside it, 409 pool/retired.
  PROVIDER_NUMBERS_PROVIDERS: '/v1/numbers/provider/providers',
  PROVIDER_NUMBERS_MINE: '/v1/numbers/provider/mine',
  PROVIDER_NUMBERS_TARGETS: '/v1/numbers/provider/targets',
  PROVIDER_NUMBER_HISTORY: (numberId: number) => `/v1/numbers/provider/${numberId}/history`,
  PROVIDER_NUMBER_REASSIGN: (numberId: number) => `/v1/numbers/provider/${numberId}/reassign`,
  PROVIDER_NUMBER_RETURN: (numberId: number) => `/v1/numbers/provider/${numberId}/return`,

  // Capacity limits (number/provider/org/campaign tiers)
  CAPACITY_LIMITS: '/telephony/capacity-limits',
  CAPACITY_LIMIT_DETAIL: (id: number) => `/telephony/capacity-limits/${id}`,
  CAPACITY_LIMITS_RESOLVE: '/telephony/capacity-limits/resolve',
  CAPACITY_ALLOCATION: '/telephony/capacity-limits/allocation',
  CAPACITY_LIMITS_HISTORY: '/telephony/capacity-limits/history',
  // "Enforced on switch" panel + trunk capacity changes (master / Green Giraffe only)
  CAPACITY_ENFORCEMENT: '/telephony/capacity-enforcement',
  CAPACITY_ENFORCEMENT_RECORD: '/telephony/capacity-enforcement/record',
  CAPACITY_TRUNK_CHANGE: (providerId: number) => `/telephony/capacity-enforcement/trunks/${providerId}/capacity`,
  // Master "Push to switch" per limit: preview → apply (read back) → sync status
  CAPACITY_PUSH_PREVIEW: (limitId: number) => `/telephony/capacity-enforcement/limits/${limitId}/push/preview`,
  CAPACITY_PUSH: (limitId: number) => `/telephony/capacity-enforcement/limits/${limitId}/push`,
  CAPACITY_LIMIT_SYNC: (limitId: number) => `/telephony/capacity-enforcement/limits/${limitId}/sync`,
  TELEPHONY_USAGE: '/telephony/usage',
  // Multi-tenant SIP seats. Org-scoped server-side: an org admin sees only their
  // own, a master admin may pass organizationId to act on another.
  EXTENSIONS: '/v1/telephony/extensions',
  EXTENSION_ASSIGNEE: (id: string) => `/v1/telephony/extensions/${id}/assignee`,
  EXTENSION_ENABLED: (id: string) => `/v1/telephony/extensions/${id}/enabled`,
  // The team directory: the one telephony route an ORDINARY agent may read.
  // EXTENSIONS above is admin-only and returns the seat entity including its SIP
  // password, so a colleague list must not come from it.
  DIRECTORY: '/v1/telephony/directory',
  DIRECTORY_LOOKUP: (extension: string) => `/v1/telephony/directory/${extension}`,
  EXTENSION_NAME: (id: string) => `/v1/telephony/extensions/${id}/name`,
  EXTENSION_RESET_PASSWORD: (id: string) => `/v1/telephony/extensions/${id}/reset-password`,
  EXTENSION_DELETE: (id: string) => `/v1/telephony/extensions/${id}`,
  // Which numbers a seat may present when dialling out. Server-enforced: the
  // list only ever contains numbers the organization holds on its routed trunk.
  EXTENSION_CALLER_IDS: (id: string) => `/v1/telephony/extensions/${id}/caller-ids`,
  // Follow-me: an unanswered seat chases the agent's own numbers. Admin-set,
  // because each firing is a billed outbound call on the org's trunk.
  EXTENSION_FOLLOW_ME: (id: string) => `/v1/telephony/extensions/${id}/follow-me`,
  EXTENSION_FOLLOW_ME_ENABLED: (id: string) => `/v1/telephony/extensions/${id}/follow-me/enabled`,
  // Rendered config, and the push that applies it to a node.
  EXTENSION_CONFIG_PREVIEW: '/v1/telephony/extensions/config/preview',
  EXTENSION_CONFIG_PUSH: '/v1/telephony/extensions/config/push',

  // ACD groups
  GROUPS: '/v1/telephony/groups',
  GROUP_BY_ID: (id: string) => `/v1/telephony/groups/${id}`,
  GROUP_MEMBERS: (id: string) => `/v1/telephony/groups/${id}/members`,
  // Issues a mailbox PIN and returns it ONCE. There is deliberately no GET —
  // the value is stored so Asterisk can be handed it in voicemail.conf, not so
  // it can be read back. Losing it means issuing another.
  GROUP_VOICEMAIL_PIN: (id: string) => `/v1/telephony/groups/${id}/voicemail-pin`,

  // IVR app registry (one per configured operator_id/service_id flow)
  IVR_APPS: '/telephony/ivr/apps',
  IVR_APP_DETAIL: (id: number) => `/telephony/ivr/apps/${id}`,
  IVR_APPS_IMPORT: '/telephony/ivr/apps/import',

  // Worker runtime config (cdr-worker, webhook-consumer, …)
  WORKER_CONFIGS: '/workers/config',
  WORKER_CONFIG_DETAIL: (workerName: string) => `/workers/config/${workerName}`,
  WORKER_CONFIG_BY_ID: (id: number) => `/workers/config/${id}`,

  // Users
  USERS: '/users',
  USERS_BY_ORG: (organizationId: string) => `/users?organizationId=${organizationId}`,
  USER_DETAIL: (id: number) => `/users/${id}`,
  USER_SEARCH: '/users/search',

  // Dashboard Stats
  DASHBOARD_STATS: '/dashboard/stats',
  DASHBOARD_CHARTS: '/dashboard/charts',

  // Reports (Aggregated)
  REPORTS_HOURLY: '/hourly-reports',
  REPORTS_DAILY: '/daily-reports',

  // DTMF Events
  DTMF_EVENTS: '/dtmf',
  DTMF_BY_CALL: (callId: number) => `/dtmf/call/${callId}`,

  // Custom Reports
  CUSTOM_REPORTS: '/custom-reports',
  CUSTOM_REPORT_DETAIL: (id: number) => `/custom-reports/${id}`,
  CUSTOM_REPORT_DOWNLOAD: (id: number) => `/custom-reports/${id}/download`,

  // Business Hours
  BUSINESS_HOURS: '/business-hours',
  BUSINESS_HOURS_CHECK: '/business-hours/check',
  BUSINESS_HOURS_IS_OPEN: '/business-hours/is-open',
  BUSINESS_HOURS_EXISTS: '/business-hours/exists',

  // Campaigns
  CAMPAIGNS: '/campaigns',
  CAMPAIGN_DETAIL: (id: number) => `/campaigns/${id}`,
  CAMPAIGN_ACTIVATE: (id: number) => `/campaigns/${id}/activate`,
  CAMPAIGN_PAUSE: (id: number) => `/campaigns/${id}/pause`,
  CAMPAIGN_RESUME: (id: number) => `/campaigns/${id}/resume`,
  CAMPAIGN_COMPLETE: (id: number) => `/campaigns/${id}/complete`,
  CAMPAIGN_ARCHIVE: (id: number) => `/campaigns/${id}/archive`,
  CAMPAIGN_STATISTICS: (id: number) => `/campaigns/${id}/statistics`,
  CAMPAIGN_SEARCH: '/campaigns/search',

  // DND (Do Not Disturb)
  DND: '/dnd',
  DND_DETAIL: (id: number) => `/dnd/${id}`,
  DND_CHECK: '/dnd/check',
  DND_IS_BLOCKED: '/dnd/is-blocked',
  DND_SEARCH: '/dnd/search',
  DND_STATS: '/dnd/stats',
  DND_BULK_IMPORT: '/dnd/bulk-import',
  DND_EXPORT: '/dnd/export',

  // VIP Callers
  VIP: '/vip',
  VIP_DETAIL: (id: number) => `/vip/${id}`,
  VIP_CHECK: '/vip/check',
  VIP_IS_VIP: '/vip/is-vip',
  VIP_SEARCH: '/vip/search',
  VIP_STATS: '/vip/stats',
  VIP_TOP: '/vip/top',
  VIP_BULK_IMPORT: '/vip/bulk-import',
  VIP_EXPORT: '/vip/export',

  // Unified Contacts (cross-channel)
  CONTACTS: '/v1/contacts',
  CONTACT_DETAIL: (uid: string) => `/v1/contacts/${uid}`,
  CONTACT_CREATE: '/v1/contacts',
  CONTACT_UPDATE: (uid: string) => `/v1/contacts/${uid}`,
  CONTACT_DELETE: (uid: string) => `/v1/contacts/${uid}`,
  CONTACTS_BULK_IMPORT: '/v1/contacts/import',
  CONTACTS_IMPORT_TEMPLATE: '/v1/contacts/import/template',
  CONTACTS_EXPORT: '/v1/contacts/export',
  CONTACT_INTERACTIONS: (uid: string) => `/v1/contacts/${uid}/interactions`,
  // The real merge is POST /v1/contacts/{id}/merge — the survivor is in the path.
  // This flat path never existed on the backend; the service method that used it was
  // dead code and has been removed.
  CONTACT_MERGE: '/v1/contacts/merge',
  CONTACT_SEARCH: '/v1/contacts/search',
  CONTACT_PHONE: (phoneNumber: string) => `/v1/contacts/phone/${phoneNumber}`,
  CONTACT_STATS: '/v1/contacts/stats',

  // WhatsApp Configuration (Backend API v1)
  WHATSAPP_CONFIG: '/v1/whatsapp/config',
  WHATSAPP_CONFIG_ALL: '/v1/whatsapp/config/all', // Get ALL configs for current org
  WHATSAPP_CONFIG_ALL_ORG: (organizationId: string) => `/v1/whatsapp/config/all/${organizationId}`, // Get ALL configs for specific org
  WHATSAPP_CONFIG_ORG: (organizationId: string) => `/v1/whatsapp/config/organization/${organizationId}`,
  WHATSAPP_CONFIG_BY_ID: (configId: string) => `/v1/whatsapp/config/${configId}`, // Update/Delete specific config
  WHATSAPP_CONFIG_BUSINESSES: '/v1/whatsapp/config/businesses',
  WHATSAPP_CONFIG_REGISTER: '/v1/whatsapp/config/register',
  WHATSAPP_CONFIG_REGISTER_BY_ID: (configId: string) => `/v1/whatsapp/config/${configId}/register`,
  WHATSAPP_CONFIG_SUBSCRIBE_WEBHOOKS: '/v1/whatsapp/config/subscribe-webhooks',
  WHATSAPP_CONFIG_SUBSCRIBE_WEBHOOKS_BY_ID: (configId: string) => `/v1/whatsapp/config/${configId}/subscribe-webhooks`,
  WHATSAPP_CONFIG_SUBSCRIBE_WEBHOOKS_BY_WABA: (wabaId: string) => `/v1/whatsapp/config/waba/${wabaId}/subscribe-webhooks`,

  // WhatsApp Embedded Signup Config (Backend API v1)
  WHATSAPP_EMBEDDED_SIGNUP_CONFIG: (organizationId: string) => `/v1/whatsapp/embedded-signup/config/inherit/${organizationId}`,
  WHATSAPP_EMBEDDED_SIGNUP_CONFIG_CRUD: '/v1/whatsapp/embedded-signup/config',
  WHATSAPP_EMBEDDED_SIGNUP_CONFIG_BY_ID: (id: string) => `/v1/whatsapp/embedded-signup/config/${id}`,
  WHATSAPP_EMBEDDED_SIGNUP_CALLBACK: '/v1/whatsapp/embedded-signup/callback',
  WHATSAPP_EMBEDDED_SIGNUP_REGISTRATIONS: '/v1/whatsapp/embedded-signup/registrations',
  WHATSAPP_EMBEDDED_SIGNUP_RESYNC: '/v1/whatsapp/embedded-signup/resync',
  WHATSAPP_EMBEDDED_SIGNUP_DISCONNECT: '/v1/whatsapp/embedded-signup/disconnect',
  WHATSAPP_EMBEDDED_SIGNUP_REACTIVATE: '/v1/whatsapp/embedded-signup/reactivate',

  // WhatsApp Templates (Backend API v1)
  WHATSAPP_TEMPLATES: '/v1/whatsapp/templates',
  WHATSAPP_TEMPLATE_HEADER_MEDIA: '/v1/whatsapp/templates/header-media',
  WHATSAPP_TEMPLATE_DETAIL: (id: string) => `/v1/whatsapp/templates/${id}`,
  WHATSAPP_TEMPLATE_APPROVED: '/v1/whatsapp/templates/approved',
  WHATSAPP_TEMPLATE_STATUS: (id: string) => `/v1/whatsapp/templates/${id}/status`,
  WHATSAPP_TEMPLATE_STATS: '/v1/whatsapp/templates/stats',
  WHATSAPP_TEMPLATE_SYNC: '/v1/whatsapp/templates/sync',

  // WhatsApp Campaigns (Backend API v1)
  WHATSAPP_CAMPAIGNS: '/v1/whatsapp/campaigns',
  OPERATOR: '/v1/operator',
  WHATSAPP_CAMPAIGN_DETAIL: (id: string) => `/v1/whatsapp/campaigns/${id}`,
  // PATCH — only the changed fields, and only those the DTO's editableFields allows.
  WHATSAPP_CAMPAIGN_UPDATE: (id: string) => `/v1/whatsapp/campaigns/${id}`,
  WHATSAPP_CAMPAIGN_START: (id: string) => `/v1/whatsapp/campaigns/${id}/start`,
  WHATSAPP_CAMPAIGN_PAUSE: (id: string) => `/v1/whatsapp/campaigns/${id}/pause`,
  WHATSAPP_CAMPAIGN_CANCEL: (id: string) => `/v1/whatsapp/campaigns/${id}/cancel`,
  WHATSAPP_CAMPAIGN_INSIGHTS: (id: string) => `/v1/whatsapp/campaigns/${id}/insights`,
  WHATSAPP_CAMPAIGN_RESPONDERS: (id: string) => `/v1/whatsapp/campaigns/${id}/responders`,
  WHATSAPP_CAMPAIGN_STATS: '/v1/whatsapp/campaigns/stats',
  WHATSAPP_TEMPLATE_INSIGHTS: (id: string, days: number) => `/v1/whatsapp/templates/${id}/insights?days=${days}`,

  // WhatsApp Messages (Backend API v1)
  WHATSAPP_MESSAGES: '/v1/whatsapp/messages',
  WHATSAPP_CONTACT_MESSAGES: (contactId: string) => `/v1/whatsapp/messages/contact/${contactId}`,
  WHATSAPP_MESSAGE_DETAIL: (id: string) => `/v1/whatsapp/messages/${id}`,
  WHATSAPP_MESSAGE_SEND: '/v1/whatsapp/messages/send',
  WHATSAPP_MESSAGE_SEND_TEXT: '/v1/whatsapp/messages/send/text',
  WHATSAPP_MESSAGE_SEND_TEMPLATE: '/v1/whatsapp/messages/send/template',
  WHATSAPP_MESSAGE_MARK_READ: (messageId: string) => `/v1/whatsapp/messages/${messageId}/read`,

  // WhatsApp Reports (Backend API v1)
  WHATSAPP_REPORTS_HOURLY: '/v1/whatsapp/reports/hourly',
  WHATSAPP_REPORTS_LATEST: '/v1/whatsapp/reports/latest',
  WHATSAPP_REPORTS_STATS: '/v1/whatsapp/reports/stats',
  WHATSAPP_REPORTS_ANALYTICS: '/v1/whatsapp/reports/analytics',

  // WhatsApp Webhook (Backend API v1)
  WHATSAPP_WEBHOOK: '/v1/whatsapp/webhook',

  // WhatsApp Partner/Credit Line (Admin API v1)
  WHATSAPP_PARTNER_REGISTRATIONS: '/v1/whatsapp/partner/registrations',
  WHATSAPP_PARTNER_REGISTRATION_DETAIL: (registrationId: string) => `/v1/whatsapp/partner/registrations/${registrationId}`,
  WHATSAPP_PARTNER_LINK_CREDIT_LINE: '/v1/whatsapp/partner/link-credit-line',
  WHATSAPP_PARTNER_LINK_CREDIT_LINE_BY_REGISTRATION: (registrationId: string) => `/v1/whatsapp/partner/registrations/${registrationId}/link-credit-line`,

  // Audit Logs
  AUDIT_LOGS: '/audit',
  AUDIT_BY_ORGANIZATION: (organizationId: string) => `/audit/organization/${organizationId}`,
  AUDIT_RESOURCE_HISTORY: (resourceType: string, resourceId: string) => `/audit/resource/${resourceType}/${resourceId}`,
  AUDIT_USER_ACTIVITY: (userId: string) => `/audit/user/${userId}`,
  AUDIT_LOGINS: '/audit/logins',
  AUDIT_BY_ACTION: (actionType: string) => `/audit/actions/${actionType}`,
  AUDIT_BY_SOURCE: (triggerSource: string) => `/audit/source/${triggerSource}`,
};